/**
 * /become-an-ambassador — public pitch page for the Ox Ambassador programme.
 *
 * Two responsibilities:
 *   1. Sell the programme (commission on every customer they bring, live
 *      dashboard, no lock-in) so an installer landing here decides to apply.
 *   2. Capture an application → `POST /api/public/ambassador-applications`
 *      which drops into the Staff → Ambassadors → Applications queue for
 *      review + one-click "Approve" that mints the affiliate.
 *
 * No auth required. Rate-limited by the backend to one application per
 * email per 24h so a form-spammer bot can't flood the queue.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle2, TrendingUp, Users, Wallet, ArrowRight, Sparkles } from 'lucide-react';

import API_URL from '@/config/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { formatApiError } from '@/utils/apiError';

const BENEFITS = [
  {
    icon: TrendingUp,
    title: 'Earn on every customer you bring',
    body: 'Set your own commission rate per product line — Bound, Epoxy, Decor — and get paid every time an order settles.',
  },
  {
    icon: Users,
    title: 'Your own branded signup link',
    body: 'Share a personalised /join/your-name link — installers who sign up through it are permanently attributed to you.',
  },
  {
    icon: Wallet,
    title: 'Live dashboard',
    body: 'See your customers, their spend, and your monthly earnings in real time. No spreadsheets, no waiting on statements.',
  },
];

const AUDIENCE_OPTIONS = [
  { value: 'installers', label: 'Other installers' },
  { value: 'customers', label: 'Homeowners / customers' },
  { value: 'social', label: 'Social media following' },
  { value: 'trade', label: 'Trade network' },
  { value: 'other', label: 'Other' },
];

export default function BecomeAmbassadorLanding() {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', company_name: '', audience: 'installers', pitch: '',
  });
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Please add your name and email so we can get back to you.');
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/public/ambassador-applications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(formatApiError(data) || 'Submission failed');
      setSubmitted(true);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-amber-50">
      {/* Hero */}
      <section className="max-w-5xl mx-auto px-4 pt-16 pb-10 text-center">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-purple-100 text-[#7A0B7A] px-3 py-1 text-xs font-bold uppercase tracking-wider">
          <Sparkles className="w-3.5 h-3.5" /> Ox Ambassador Programme
        </span>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mt-6 leading-tight">
          Bring customers, earn commission.
        </h1>
        <p className="text-base text-gray-600 mt-4 max-w-2xl mx-auto">
          Ox Resin partners with a handful of trusted names to grow the network. Introduce customers through your own link, and earn a percentage of every order they settle — for as long as they buy.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <a href="#apply">
            <Button className="bg-[#7A0B7A] hover:bg-[#5e0860] text-white" data-testid="apply-scroll-cta">
              Apply now <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </a>
          <Link to="/login">
            <Button variant="outline">Already an Ambassador? Sign in</Button>
          </Link>
        </div>
      </section>

      {/* Benefits */}
      <section className="max-w-5xl mx-auto px-4 py-8">
        <div className="grid md:grid-cols-3 gap-4">
          {BENEFITS.map((b, i) => (
            <Card key={i} className="p-6" data-testid={`benefit-${i}`}>
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center mb-3">
                <b.icon className="w-5 h-5 text-[#7A0B7A]" />
              </div>
              <h3 className="font-bold text-gray-900">{b.title}</h3>
              <p className="text-sm text-gray-600 mt-2">{b.body}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-5xl mx-auto px-4 py-10">
        <h2 className="text-2xl font-bold text-gray-900 text-center mb-6">How it works</h2>
        <div className="grid sm:grid-cols-4 gap-4 text-sm">
          {[
            { n: '1', t: 'Apply', d: 'Fill in the form below — takes 60 seconds.' },
            { n: '2', t: 'We approve', d: 'Ox reviews and greenlights you within 2 business days.' },
            { n: '3', t: 'Share your link', d: 'Get a branded /join/your-name link to hand out.' },
            { n: '4', t: 'Get paid', d: 'Every time your customer settles an order, commission lands in your dashboard.' },
          ].map((s) => (
            <div key={s.n} className="flex gap-3 items-start">
              <div className="w-8 h-8 rounded-full bg-[#7A0B7A] text-white text-sm font-bold flex items-center justify-center shrink-0">{s.n}</div>
              <div>
                <p className="font-bold text-gray-900">{s.t}</p>
                <p className="text-gray-600 mt-0.5">{s.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Application form */}
      <section id="apply" className="max-w-2xl mx-auto px-4 py-12">
        <Card className="p-6 md:p-8" data-testid="ambassador-apply-card">
          {submitted ? (
            <div className="text-center py-10">
              <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
              <h3 className="text-xl font-bold text-gray-900">Thanks — application received.</h3>
              <p className="text-sm text-gray-600 mt-2">
                We'll be in touch within 2 business days at <span className="font-semibold">{form.email}</span>. Once approved, you'll get your unique link and dashboard login.
              </p>
              <Link to="/"><Button variant="outline" className="mt-6">Back to home</Button></Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Apply to become an Ambassador</h3>
                <p className="text-sm text-gray-600 mt-1">Tell us a bit about you and how you'd introduce customers to Ox.</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>Your name *</Label>
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="apply-name" />
                </div>
                <div>
                  <Label>Company (optional)</Label>
                  <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="apply-email" />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Who's your audience?</Label>
                <select
                  className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                  value={form.audience}
                  onChange={(e) => setForm({ ...form, audience: e.target.value })}
                  data-testid="apply-audience"
                >
                  {AUDIENCE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <Label>Why would you be a great Ambassador?</Label>
                <Textarea
                  rows={4}
                  placeholder="Give us the pitch — how many people you'd reach, what you'd say, past experience introducing trades…"
                  value={form.pitch}
                  onChange={(e) => setForm({ ...form, pitch: e.target.value })}
                  data-testid="apply-pitch"
                />
              </div>
              <Button type="submit" disabled={busy} className="w-full bg-[#7A0B7A] hover:bg-[#5e0860] text-white" data-testid="apply-submit">
                {busy ? 'Submitting…' : 'Submit application'}
              </Button>
              <p className="text-[11px] text-gray-500 text-center">By submitting you agree to be contacted about the Ambassador programme.</p>
            </form>
          )}
        </Card>
      </section>
    </div>
  );
}
